import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { MailIcon, PhoneIcon, MapPinIcon, CheckIcon } from 'lucide-react';
import { SectionLabel } from './SectionLabel';
const WHATSAPP_URL = 'https://wa.me/59895120626';
const CONTACT_EMAIL_ENDPOINT = 'https://formsubmit.co/ajax/hola@vivomedia.uy';
type SubmissionStatus = 'idle' | 'sending' | 'success' | 'error';
const FIELDS = [{
  name: 'nombre',
  label: 'Nombre completo',
  type: 'text'
}, {
  name: 'empresa',
  label: 'Empresa',
  type: 'text'
}, {
  name: 'email',
  label: 'Email',
  type: 'email'
}, {
  name: 'telefono',
  label: 'Teléfono',
  type: 'tel'
}] as const;
export function Contact() {
  const [status, setStatus] = useState<SubmissionStatus>('idle');
  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setStatus('sending');
    const form = e.currentTarget;
    const formData = new FormData(form);
    formData.append('_subject', 'Nueva consulta desde Vivo Media');
    formData.append('_template', 'table');
    formData.append('_captcha', 'false');
    try {
      const response = await fetch(CONTACT_EMAIL_ENDPOINT, {
        method: 'POST',
        headers: {
          Accept: 'application/json'
        },
        body: formData
      });
      if (!response.ok) {
        throw new Error('No se pudo enviar el formulario.');
      }
      form.reset();
      setStatus('success');
    } catch {
      setStatus('error');
    }
  };
  const inputClass = 'w-full rounded-2xl border border-vm-muted/50 bg-vm-bg px-4 py-3 font-light text-white placeholder-vm-muted transition-colors focus:border-vm-electric focus:outline-none';
  return <section id="contacto" className="w-full bg-vm-bg py-28 lg:py-36" data-id="element-48" data-mp="2s6849b3xn50y">
      <div className="max-w-7xl mx-auto px-6 lg:px-10" data-id="element-49" data-mp="2w71r015n2ds9">
        <div className="grid gap-12 lg:grid-cols-2 lg:gap-20" data-id="element-50" data-mp="qd22tzl2ftam">
          <motion.div initial={{
          opacity: 0,
          y: 24
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true,
          margin: '-80px'
        }} transition={{
          duration: 0.6
        }} data-id="element-51" data-mp="qdk0n0ndmfkn">
            <SectionLabel data-id="element-52" data-mp="2397qbvi5u6mk">Contacto</SectionLabel>
            <h2 className="mt-6 font-syne text-4xl font-bold leading-[1.03] text-white sm:text-5xl lg:text-[56px]" data-id="element-53" data-mp="1ybfwgvxygafq">
              ¿Tenés un proyecto en mente? Hablemos.
            </h2>
            <p className="mt-4 text-vm-muted font-light text-lg" data-id="element-54" data-mp="1pckrfn6nd6e0">
              Respondemos en menos de 24 horas.
            </p>

            <form onSubmit={handleSubmit} className="mt-10 space-y-5" data-id="element-55" data-mp="2m1k1n7qski8u">
              <div className="grid sm:grid-cols-2 gap-5" data-id="element-56" data-mp="2i088wtcmfgwf">
                {FIELDS.map(field => <div key={field.name} data-id="element-57" data-mp="2d8yny620p1qv">
                    <label htmlFor={field.name} className="sr-only" data-id="element-58" data-mp="2d7nhtzl3nhwl">
                      {field.label}
                    </label>
                    <input id={field.name} name={field.name} type={field.type} placeholder={field.label} required className={inputClass} data-id="element-59" data-mp="35psulk05ss9e" />
                  </div>)}
              </div>
              <div data-id="element-60" data-mp="3da3n84zu4jr9">
                <label htmlFor="servicio" className="sr-only" data-id="element-61" data-mp="3dbetqcko05m2">
                  ¿Qué servicio te interesa?
                </label>
                <select id="servicio" name="servicio" required defaultValue="" className={inputClass + ' text-white/30'} data-id="element-62" data-mp="3mart2cf2rdt2">
                  <option value="" disabled data-id="element-63" data-mp="3r8bkyf4p3p3h">
                    ¿Qué servicio te interesa?
                  </option>
                  <option value="LED" className="text-white" data-id="element-64" data-mp="1h53b5c0g2c">LED</option>
                  <option value="Streaming" className="text-white" data-id="element-65" data-mp="4utggjl02qd5">Streaming</option>
                  <option value="Diseño" className="text-white" data-id="element-66" data-mp="9td2ejzlg25i">Diseño</option>
                  <option value="Fotografía" className="text-white" data-id="element-67" data-mp="duglrgguogbh">Fotografía</option>
                  <option value="Señalética" className="text-white" data-id="element-68" data-mp="dslxptaa8836">Señalética</option>
                </select>
              </div>
              <div data-id="element-69" data-mp="56au5kdcj28j">
                <label htmlFor="mensaje" className="sr-only" data-id="element-70" data-mp="55rgifuqb00b">
                  ¿En qué podemos ayudarte?
                </label>
                <textarea id="mensaje" name="mensaje" rows={5} placeholder="¿En qué podemos ayudarte?" required className={inputClass + ' resize-none'} data-id="element-71" data-mp="14z63ja4ep2g" />
              </div>
              <button type="submit" disabled={status === 'sending'} className="inline-flex w-full items-center justify-center gap-2 rounded-2xl bg-vm-sky px-8 py-4 text-sm font-semibold uppercase tracking-[0.1em] text-vm-bg shadow-lg shadow-black/20 transition-all duration-200 hover:-translate-y-0.5 hover:bg-vm-cream focus:outline-none focus-visible:ring-2 focus-visible:ring-vm-sky focus-visible:ring-offset-4 focus-visible:ring-offset-vm-bg disabled:cursor-wait disabled:opacity-70 sm:w-auto" data-id="element-72" data-mp="3idmecv134se9">
                {status === 'success' ? <>
                    <CheckIcon className="h-5 w-5" data-id="element-73" data-mp="2piwo44ho0d7o" /> Mensaje enviado
                  </> : status === 'sending' ? 'Enviando...' : 'Enviar mensaje'}
              </button>
              <div aria-live="polite" data-id="element-74" data-mp="2gii0gyh8am10">
                {status === 'success' && <p className="mt-4 text-sm text-vm-muted" data-id="element-75" data-mp="3493vnp64wbtl">Gracias, recibimos tu mensaje. Te responderemos pronto.</p>}
                {status === 'error' && <p className="mt-4 text-sm text-vm-cream" data-id="element-76" data-mp="2uisrq2mga2mp">No pudimos enviar tu mensaje. Intentá nuevamente o escribinos por WhatsApp.</p>}
              </div>
            </form>
          </motion.div>

          <motion.div initial={{
          opacity: 0,
          y: 24
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true,
          margin: '-80px'
        }} transition={{
          duration: 0.6,
          delay: 0.1
        }} className="lg:border-l lg:border-white/10 lg:pl-10" data-id="element-77" data-mp="3n5wq3or1fipi">
            <div className="space-y-8" data-id="element-78" data-mp="q7qp0sgninf">
              <ContactItem icon={MailIcon} label="Email" value="hola@vivomedia.uy" href="mailto:hola@vivomedia.uy" data-id="element-79" data-mp="n24puf4owvx" />
              <ContactItem icon={PhoneIcon} label="WhatsApp" value="+598 95 120 626" href={WHATSAPP_URL} data-id="element-80" data-mp="3mwd19k2xt2y9" />
              <ContactItem icon={MapPinIcon} label="Ubicación" value="Montevideo, Uruguay" data-id="element-81" data-mp="3iv1ahb8buboh" />
            </div>

            <a href={WHATSAPP_URL} target="_blank" rel="noopener noreferrer" className="mt-12 inline-flex items-center justify-center gap-3 rounded-2xl bg-vm-sky px-7 py-4 text-sm font-semibold uppercase tracking-[0.1em] text-vm-bg shadow-lg shadow-black/20 transition-all duration-200 hover:-translate-y-0.5 hover:bg-vm-cream focus:outline-none focus-visible:ring-2 focus-visible:ring-vm-sky focus-visible:ring-offset-4 focus-visible:ring-offset-vm-bg" data-id="element-82" data-mp="3e3j6sfex5wzt">
              <svg viewBox="0 0 24 24" className="h-5 w-5 fill-vm-bg" aria-hidden="true" data-id="element-83" data-mp="1amcwjkcv48oi">
                <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.372-.025-.521-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51l-.57-.01c-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.71.306 1.263.489 1.694.625.712.227 1.36.195 1.872.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" data-id="element-84" data-mp="1en6jobihdjgc" />
              </svg>
              Escribinos por WhatsApp
            </a>
          </motion.div>
        </div>
      </div>
    </section>;
}
interface ContactItemProps {
  icon: React.ElementType;
  label: string;
  value: string;
  href?: string;
}
function ContactItem({
  icon: Icon,
  label,
  value,
  href
}: ContactItemProps) {
  const content = <div className="flex items-start gap-4" data-id="element-85" data-mp="3ugi7bdixq790">
      <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl border border-vm-cream bg-vm-cream text-vm-bg shadow-sm shadow-black/20" data-id="element-86" data-mp="3ufglqf4am2d1">
        <Icon className="h-5 w-5" data-id="element-87" data-mp="89a4ahvhmcg5" />
      </div>
      <div data-id="element-88" data-mp="8a1okwjmwnr4">
        <div className="text-xs font-semibold uppercase tracking-[0.25em] text-white" data-id="element-89" data-mp="87fk55s1iod4">{label}</div>
        <div className="mt-1 text-lg font-normal text-vm-sky" data-id="element-90" data-mp="38cebj48ke43">{value}</div>
      </div>
    </div>;
  return href ? <a href={href} className="block hover:opacity-80 transition-opacity" data-id="element-91" data-mp="mue64l84oqkg">
      {content}
    </a> : content;
}